import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Row, Col, Space, Typography, Table, Select, Form, Input, message, Modal } from 'antd';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import DispatchCheckerService from '../../../services/Dispatch-Checker/DispatchCheckerService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { DeleteOutlined, CheckCircleOutlined,QrcodeOutlined } from '@ant-design/icons';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';
import beepSound from '../../../assets/dashboard/scan-beep.mp3';

const { Text } = Typography;

const CapaReportPage = () => {
  const { state } = useLocation();
  const [form] = Form.useForm();
  const userData = JSON.parse(localStorage.getItem('userData'));
  const [selectedLine, setSelectedLine] = useState('');
  const [date, setDate] = useState(moment().startOf('day'));
  const [itemNo, setItemNo] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userName] = useState(userData?.data?.full_name || 'Unknown User');
  const [dueDate, setDueDate] = useState(moment().add(1, 'month').toDate());
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [reviewInfo, setReviewInfo] = useState(null); // To store single review info
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const beepSoundRef = useRef(new Audio(beepSound));
  const isScanProcessed = useRef(false);

  // Initial data load and cleanup
  useEffect(() => {
    console.log('Initial date set to:', date.format('YYYY-MM-DD'));
    if (userData?.data?.su_id) {
      fetchLinesAndItems(userData.data.su_id);
    }
    if (state?.line_no) {
      setSelectedLine(state.line_no);
    } else if (state?.item_no) {
      setItemNo(state.item_no);
    }
    return () => {
      stopScanning();
    };
  }, [state?.line_no, state?.item_no, userData?.data?.su_id]);

  // Fetch data when line, item, or date changes
  useEffect(() => {
    if (selectedLine && date && itemNo) {
      fetchCapaData();
    }
  }, [selectedLine, date, itemNo]);

  const fetchLinesAndItems = async (su_id) => {
    setLoading(true);
    try {
      const requestData = { su_id };
      console.log('get_userline_and_items Request:', requestData);
      const response = await DispatchCheckerService.get_userline_and_items(su_id);
      console.log('get_userline_and_items Response:', response);
      if (response.status && Array.isArray(response.line_data)) {
        setLineItems(response.line_data);
        if (response.line_data.length > 0 && !selectedLine) {
          setSelectedLine(response.line_data[0].line_no);
          const selectedLineData = response.line_data[0];
          setItemNo(selectedLineData?.items[0]?.item_no || '');
        }
      } else {
        console.warn('Invalid response format:', response);
        setLineItems([]);
        message.error(response.message || 'No lines or items available');
      }
    } catch (error) {
      console.error('Error fetching lines and items:', error);
      setLineItems([]);
      message.error('Failed to fetch lines and items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCapaData = async () => {
    if (!selectedLine || !date || !itemNo) return;
    setLoading(true);
    try {
      const requestData = {
        line_no: selectedLine,
        item_no: itemNo,
        added_on: date.format('YYYY-MM-DD'),
      };
      console.log('get_eol_capa_by_filter Request:', requestData);
      const response = await DispatchCheckerService.get_eol_capa_by_filter(selectedLine, itemNo, date.format('YYYY-MM-DD'));
      console.log('get_eol_capa_by_filter Response:', response);
      if (response.status) {
        const capaData = response.data.map(item => ({
          key: item.ecr_id,
          ecr_id: item.ecr_id,
          finding_observation: item.finding_observation,
          root_cause: item.root_cause,
          correction_immediate: item.correction_Immediate,
          corrective_action: item.corrective_action,
          prevention_plan: item.prevention_plan,
          person_responsible: item.person_responsible,
          due_date: item.due_date,
          status: item.status,
          review_by: item.review_by,
          review_dates: item.review_dates || [],
        }));
        setTableData(capaData);
        // Set single review info if any review_by exists
        const reviewedItem = capaData.find(item => item.review_by && item.review_by.trim() !== '');
        if (reviewedItem && reviewedItem.review_dates.length > 0) {
          setReviewInfo({
            reviewer: reviewedItem.review_by,
            date: reviewedItem.review_dates[0],
          });
        } else {
          setReviewInfo(null);
        }
      } else {
        setTableData([]);
        message.error(response.message || 'Failed to fetch CAPA data');
        setReviewInfo(null);
      }
    } catch (error) {
      console.error('Error fetching CAPA data:', error);
      message.error('An error occurred while fetching CAPA data');
      setTableData([]);
      setReviewInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLineChange = (value) => {
    setSelectedLine(value);
    const selectedLineData = lineItems.find(line => line.line_no === value);
    setItemNo(selectedLineData?.items[0]?.item_no || '');
  };

  const handleDateChange = (date) => {
    setDate(date ? moment(date) : null);
    console.log('Date changed to:', date ? moment(date).format('YYYY-MM-DD') : 'null');
  };

  const handleDueDateChange = (date) => {
    setDueDate(date);
    form.setFieldsValue({ due_date: date });
    console.log('Due date changed to:', date ? moment(date).format('YYYY-MM-DD') : 'null');
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const capaData = {
        line_no: selectedLine,
        item_no: itemNo,
        finding_observation: values.finding_observation,
        root_cause: values.root_cause,
        correction_immediate: values.correction_immediate,
        corrective_action: values.corrective_action,
        prevention_plan: values.prevention_plan,
        person_responsible: values.person_responsible,
        due_date: values.due_date ? moment(values.due_date).format('YYYY-MM-DD') : '',
        status: values.status || 'Open',
      };
      console.log('insert_eol_capa_report Request:', capaData);
      const response = await DispatchCheckerService.insert_eol_capa_report(capaData);
      console.log('insert_eol_capa_report Response:', response);
      if (response.status) {
        message.success(response.message);
        form.resetFields();
        setDueDate(moment().add(1, 'month').toDate());
        fetchCapaData();
      } else {
        message.error(response.message || 'Failed to insert CAPA report');
      }
    } catch (error) {
      console.error('Error inserting CAPA report:', error);
      message.error('An error occurred while inserting CAPA report');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ecr_id) => {
    setLoading(true);
    try {
      const requestData = { ecr_id };
      console.log('delete_eol_capa_report Request:', requestData);
      const response = await DispatchCheckerService.delete_eol_capa_report(ecr_id);
      console.log('delete_eol_capa_report Response:', response);
      if (response.status) {
        message.success(response.message);
        fetchCapaData();
      } else {
        message.error(response.message || 'Failed to delete CAPA report');
      }
    } catch (error) {
      console.error('Error deleting CAPA report:', error);
      message.error('An error occurred while deleting CAPA report');
    } finally {
      setLoading(false);
    }
  };

  const startScanning = async () => {
    setIsScanning(true);
    setScanResult('');
    isScanProcessed.current = false;
    codeReader.current = new BrowserMultiFormatReader();
    try {
      const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
      if (videoInputDevices.length === 0) {
        message.error('No camera devices found');
        setIsScanning(false);
        codeReader.current = null;
        return;
      }
      const deviceId = videoInputDevices[0].deviceId;
      codeReader.current.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
        if (result && !isScanProcessed.current) {
          isScanProcessed.current = true;
          setScanResult(result.getText());
          beepSoundRef.current.play().catch(error => console.error('Beep play error:', error));
          handleReview(result.getText());
          stopScanning();
        }
        if (err && !(err instanceof NotFoundException)) {
          console.error('Scanning error:', err);
          message.error('Error during scanning');
          stopScanning();
        }
      }, { once: true });
    } catch (error) {
      console.error('Error starting scanner:', error);
      message.error('Failed to start scanner');
      setIsScanning(false);
      codeReader.current = null;
    }
  };

  const stopScanning = () => {
    if (codeReader.current) {
      if (typeof codeReader.current.stop === 'function') {
        codeReader.current.stop();
      }
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    codeReader.current = null;
    setIsScanning(false);
    setScanResult('');
  };

  const handleReview = async (barcode) => {
    setLoading(true);
    try {
      const ecrIds = tableData.map(item => item.ecr_id).join(',');
      if (!ecrIds) {
        message.error('No CAPA reports available to review');
        setLoading(false);
        return;
      }
      const reviewData = {
        line_no: selectedLine,
        item_no: itemNo,
        ecr_id: ecrIds,
        review_date: moment().format('YYYY-MM-DD HH:mm:ss'),
        review_by: barcode,
        added_by: userData?.data?.username || 'unknown',
      };
      console.log('store_eol_capa_reviews Request:', reviewData);
      const response = await DispatchCheckerService.store_eol_capa_reviews(reviewData);
      console.log('store_eol_capa_reviews Response:', response);
      if (response.status) {
        message.success('Review recorded successfully');
        fetchCapaData();
      } else {
        message.error(response.message || 'Failed to record review');
      }
    } catch (error) {
      console.error('Error recording review:', error);
      message.error('An error occurred while recording review');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Finding / Observation', dataIndex: 'finding_observation', key: 'finding_observation', width: '20%' },
    { title: 'Root Cause', dataIndex: 'root_cause', key: 'root_cause', width: '15%' },
    { title: 'Correction(Immediate)', dataIndex: 'correction_immediate', key: 'correction_immediate', width: '15%' },
    { title: 'Corrective Action', dataIndex: 'corrective_action', key: 'corrective_action', width: '15%' },
    { title: 'Prevention Plan', dataIndex: 'prevention_plan', key: 'prevention_plan', width: '15%' },
    { title: 'Person Responsible', dataIndex: 'person_responsible', key: 'person_responsible', width: '10%' },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: '10%',
      render: (text) => (text ? moment(text).format('YYYY-MM-DD') : ''),
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: '10%' },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <Button
          type="primary"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.ecr_id)}
          size="small"
          className="action-button"
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className="capa-report-container">
      <Card className="main-card">
        <Row gutter={[16, 16]} className="filter-row">
          <Col xs={24} sm={12} md={8}>
            <Text strong>Line</Text>
            <Select
              style={{ width: '100%' }}
              value={selectedLine}
              onChange={handleLineChange}
              size="large"
              className="custom-select"
            >
              {lineItems.map(line => (
                <Select.Option key={line.line_no} value={line.line_no}>
                  {line.line_no}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Item</Text>
            <Select
              style={{ width: '100%' }}
              value={itemNo}
              onChange={setItemNo}
              size="large"
              className="custom-select"
            >
              {lineItems
                .find(line => line.line_no === selectedLine)
                ?.items.map(item => (
                  <Select.Option key={item.item_no} value={item.item_no}>
                    {item.item_no}
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Text strong>Date</Text>
            <div style={{ width: '100%' }}>
              <DatePicker
                selected={date?.toDate()}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd"
                todayButton="Today"
                placeholderText="Select date"
                className="custom-date-picker"
              />
            </div>
          </Col>
        </Row>
        <Card title="Add New CAPA Report" className="form-card">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              due_date: dueDate,
              status: 'Open',
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="finding_observation"
                  label="Finding / Observation"
                  rules={[{ required: true, message: 'Please enter finding observation' }]}
                >
                  <Input className="custom-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="root_cause"
                  label="Root Cause"
                  rules={[{ required: true, message: 'Please enter root cause' }]}
                >
                  <Input className="custom-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="correction_immediate"
                  label="Correction(Immediate)"
                  rules={[{ required: true, message: 'Please enter correction immediate' }]}
                >
                  <Input className="custom-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="corrective_action"
                  label="Corrective Action"
                  rules={[{ required: true, message: 'Please enter corrective action' }]}
                >
                  <Input className="custom-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="prevention_plan"
                  label="Prevention Plan"
                  rules={[{ required: true, message: 'Please enter prevention plan' }]}
                >
                  <Input className="custom-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="person_responsible"
                  label="Person Responsible"
                  rules={[{ required: true, message: 'Please enter person responsible' }]}
                >
                  <Input className="custom-input" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="due_date"
                  label="Due Date"
                  rules={[{ required: true, message: 'Please select due date' }]}
                >
                  <DatePicker
                    selected={dueDate}
                    onChange={handleDueDateChange}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select due date"
                    className="custom-date-picker"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select placeholder="Select status" className="custom-select">
                    <Select.Option value="Open">Open</Select.Option>
                    <Select.Option value="In Progress">In Progress</Select.Option>
                    <Select.Option value="Closed">Closed</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24} style={{ textAlign: 'center' }}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} className="submit-button">
                    Submit
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>
        <Table
          className="capa-table"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={false}
          bordered
          scroll={{ x: 800 }}
          size="middle"
        />
        <Row justify="center" className="footer-row" style={{ marginTop: '24px' }}>
          <Col>
            <Space>
              {reviewInfo ? (
                <span style={{ color: 'green' }}>
                  <CheckCircleOutlined style={{ marginRight: '8px', color: 'green' }} />
                  Reviewed by {reviewInfo.reviewer} on {reviewInfo.date}
                </span>
              ) : (
                <Button
                  type="primary"
                  icon={<QrcodeOutlined />}
                  onClick={startScanning}
                  loading={loading}
                  className="review-button"
                  disabled={isScanning}
                >
                  {isScanning ? 'Scanning...' : 'Scan Review'}
                </Button>
              )}
              {isScanning && (
                <Button
                  type="default"
                  onClick={stopScanning}
                  className="stop-button"
                >
                  Stop Scan
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </Card>
      <Modal
        title="Scan Barcode"
        visible={isScanning}
        onCancel={stopScanning}
        footer={null}
        centered
        className="scan-modal"
      >
        <div className="video-container">
          <video ref={videoRef} className="video-feed" />
          {scanResult && <Text className="scan-result">Scanned: {scanResult}</Text>}
        </div>
      </Modal>

      <style>
        {`
          .capa-report-container {
            padding: 24px;
            background-color: #f5f7fa;
            min-height: 100vh;
          }
          .main-card {
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            background: #ffffff;
            padding: 24px;
          }
          .filter-row {
            margin-bottom: 24px;
          }
          .form-card {
            border-radius: 8px;
            margin-bottom: 24px;
            background: #fafafa;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          .form-card .ant-card-head {
            background: #1d39c4;
            color: #ffffff;
            border-radius: 8px 8px 0 0;
            font-weight: 500;
          }
          .custom-input, .custom-select, .custom-date-picker {
            border-radius: 6px;
            border: 1px solid #d9d9d9;
            height: 40px;
            font-size: 14px;
            transition: all 0.3s;
          }
          .custom-input:focus, .custom-select:focus, .custom-date-picker:focus {
            border-color: #1d39c4;
            box-shadow: 0 0 0 2px rgba(29, 57, 196, 0.2);
          }
          .custom-date-picker {
            width: 100%;
            padding: 8px 12px;
            box-sizing: border-box;
          }
          .react-datepicker {
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            border-radius: 6px;
          }
          .react-datepicker__day--selected {
            background-color: #1d39c4 !important;
            color: #ffffff !important;
          }
          .react-datepicker__day--keyboard-selected {
            background-color: #597ef7 !important;
          }
          .submit-button {
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            background: #1d39c4;
            border: none;
            transition: all 0.3s;
          }
          .submit-button:hover {
            background: #597ef7;
          }
          .capa-table {
            border-radius: 8px;
            overflow: hidden;
            background: #ffffff;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          .capa-table .ant-table-thead > tr > th {
            background: #f0f5ff;
            color: #1d39c4;
            font-weight: 500;
            white-space: nowrap;
          }
          .capa-table .ant-table-tbody > tr > td {
            white-space: nowrap;
            font-size: 13px;
          }
          .action-button {
            border-radius: 4px;
            font-size: 12px;
            padding: 4px 12px;
          }
          .review-button {
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            background: #52c41a;
            border: none;
            transition: all 0.3s;
          }
          .review-button:hover {
            background: #73d13d;
          }
          .stop-button {
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            transition: all 0.3s;
          }
          .scan-modal .ant-modal-content {
            border-radius: 8px;
          }
          .video-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }
          .video-feed {
            width: 100%;
            max-width: 400px;
            height: 300px;
            border-radius: 6px;
            border: 1px solid #d9d9d9;
          }
          .scan-result {
            font-size: 14px;
            color: #1d39c4;
            font-weight: 500;
          }
          .footer-row {
            margin-top: 24px;
          }
          @media (max-width: 768px) {
            .capa-report-container {
              padding: 16px;
            }
            .main-card {
              padding: 16px;
            }
            .custom-input, .custom-select, .custom-date-picker {
              height: 36px;
              font-size: 13px;
            }
            .submit-button, .review-button, .stop-button {
              padding: 6px 16px;
              font-size: 13px;
            }
            .capa-table {
              font-size: 12px;
            }
            .capa-table .ant-table-thead > tr > th,
            .capa-table .ant-table-tbody > tr > td {
              padding: 8px 6px;
            }
            .action-button {
              padding: 4px 8px;
              font-size: 11px;
            }
            .video-feed {
              max-width: 300px;
              height: 200px;
            }
          }
          @media (max-width: 576px) {
            .capa-report-container {
              padding: 12px;
            }
            .custom-input, .custom-select, .custom-date-picker {
              height: 32px;
              font-size: 12px;
            }
            .submit-button, .review-button, .stop-button {
              padding: 4px 12px;
              font-size: 12px;
            }
            .capa-table {
              font-size: 11px;
            }
            .video-feed {
              max-width: 250px;
              height: 150px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CapaReportPage;
